import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, ChevronDown, ChevronRight, RefreshCw, Radio, ExternalLink, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { api } from '@/api';
import { toast } from 'sonner';

const PullLinks = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [combineFields, setCombineFields] = useState({});

  const loadChannels = useCallback(async () => {
    try {
      const res = await api.getChannels();
      setChannels(res.data);
      // Auto-expand first channel
      if (res.data.length > 0 && !expandedId) {
        setExpandedId(res.data[0].id);
      }
    } catch {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  const copyText = async (text, field) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const i = document.createElement('input');
      i.value = text;
      document.body.appendChild(i);
      i.select();
      document.execCommand('copy');
      document.body.removeChild(i);
    }
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const toggleCombine = (id) => {
    setCombineFields(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getHlsUrl = (playbackId) => {
    if (!playbackId) return null;
    return `https://stream.mux.com/${playbackId}.m3u8`;
  };

  const getSrtUrl = (playbackId) => {
    if (!playbackId) return null;
    return `srt://srt.mux.com?streamid=${playbackId}`;
  };

  const getCombinedRtmpUrl = (ch) => {
    if (!ch.mux_stream_key) return null;
    return `${ch.rtmp_url}/${ch.mux_stream_key}`;
  };

  const activeChannels = channels.filter(ch => ch.mux_playback_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pull-links">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Pull Links</h2>
          <p className="text-gray-400 text-sm mt-1">
            Playback URLs for embedding into other broadcasts — admin-only
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="border-sky-400/50 text-sky-300 text-xs px-3 py-1">
            {activeChannels.length}/{channels.length} channels
          </Badge>
          <Button variant="ghost" size="sm" onClick={loadChannels} className="text-gray-400 hover:text-white">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-sky-900/20 border-sky-800/40">
        <CardContent className="p-4">
          <p className="text-sky-200 text-sm">
            Share these pull links with external broadcasters to embed your streams. Each channel provides an HLS playback URL and SRT URL that can be used in OBS, vMix, Wirecast, or any broadcast tool that supports stream pulling.
          </p>
        </CardContent>
      </Card>

      {/* Channel Pull Links List */}
      {activeChannels.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Radio className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No channels with pull links available.</p>
          <p className="text-sm mt-1">Create channels in the "Manage Channels" tab first.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {activeChannels.map(ch => {
            const isExpanded = expandedId === ch.id;
            const isCombined = combineFields[ch.id] || false;
            const hlsUrl = getHlsUrl(ch.mux_playback_id);
            const srtUrl = getSrtUrl(ch.mux_playback_id);
            const combinedRtmp = getCombinedRtmpUrl(ch);

            return (
              <Card
                key={ch.id}
                className={`border transition-colors ${isExpanded ? 'bg-gray-800/60 border-sky-700/50' : 'bg-gray-800/30 border-gray-700/50 hover:border-gray-600'}`}
                data-testid={`pull-link-${ch.id}`}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleExpand(ch.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  data-testid={`pull-link-toggle-${ch.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Radio className="w-4 h-4 text-sky-400" />
                    <span className="text-white font-semibold tracking-wide uppercase text-sm">
                      {ch.name}
                    </span>
                    <Badge className={`text-[10px] px-1.5 py-0 ${ch.status === 'active' ? 'bg-red-500/80 text-white' : ch.status === 'idle' ? 'bg-yellow-600/60 text-yellow-200' : 'bg-gray-600 text-gray-300'}`}>
                      {(ch.status || 'idle').toUpperCase()}
                    </Badge>
                  </div>
                  {isExpanded
                    ? <ChevronDown className="w-5 h-5 text-gray-400" />
                    : <ChevronRight className="w-5 h-5 text-gray-400" />
                  }
                </button>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="px-5 pb-5 space-y-4 border-t border-gray-700/50 pt-4">
                    {/* Combine Fields Toggle */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleCombine(ch.id)}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                        data-testid={`combine-toggle-${ch.id}`}
                      >
                        {isCombined
                          ? <ToggleRight className="w-5 h-5 text-sky-400" />
                          : <ToggleLeft className="w-5 h-5 text-gray-500" />
                        }
                        Combine fields
                      </button>
                    </div>

                    {isCombined ? (
                      <>
                        {/* Combined HLS URL */}
                        <div>
                          <Label className="text-gray-500 text-xs mb-1.5 block">HLS Playback URL (combined)</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-sky-300 text-xs font-mono break-all" data-testid={`combined-hls-${ch.id}`}>{hlsUrl}</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText(hlsUrl, `combined-hls-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-combined-hls-${ch.id}`}
                            >
                              {copiedField === `combined-hls-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        {/* Combined SRT URL */}
                        {srtUrl && (
                          <div>
                            <Label className="text-gray-500 text-xs mb-1.5 block">SRT Playback URL (combined)</Label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                                <code className="text-purple-300 text-xs font-mono break-all" data-testid={`combined-srt-${ch.id}`}>{srtUrl}</code>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyText(srtUrl, `combined-srt-${ch.id}`)}
                                className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                                data-testid={`copy-combined-srt-${ch.id}`}
                              >
                                {copiedField === `combined-srt-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Separate Fields — Stream Link + Playback ID */}
                        <div>
                          <Label className="text-gray-500 text-xs mb-1.5 block">HLS Stream link</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-sky-300 text-xs font-mono" data-testid={`hls-base-${ch.id}`}>https://stream.mux.com/</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText('https://stream.mux.com/', `hls-base-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-hls-base-${ch.id}`}
                            >
                              {copiedField === `hls-base-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs mb-1.5 block">Playback ID (stream key)</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-amber-300 text-xs font-mono break-all" data-testid={`playback-id-${ch.id}`}>{ch.mux_playback_id}</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText(ch.mux_playback_id, `playback-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-playback-${ch.id}`}
                            >
                              {copiedField === `playback-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* SRT Section */}
                        <div className="border-t border-gray-700/40 pt-4">
                          <Label className="text-gray-500 text-xs mb-1.5 block">SRT Stream link</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-purple-300 text-xs font-mono" data-testid={`srt-base-${ch.id}`}>srt://srt.mux.com</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText('srt://srt.mux.com', `srt-base-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-srt-base-${ch.id}`}
                            >
                              {copiedField === `srt-base-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-500 text-xs mb-1.5 block">SRT Stream ID</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-purple-300 text-xs font-mono break-all" data-testid={`srt-streamid-${ch.id}`}>{ch.mux_playback_id}</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText(ch.mux_playback_id, `srt-id-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-srt-id-${ch.id}`}
                            >
                              {copiedField === `srt-id-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* RTMP Ingest (for reference) */}
                    <div className="border-t border-gray-700/40 pt-4">
                      <Label className="text-gray-500 text-xs mb-1.5 block">RTMP Ingest URL (for pushing TO this channel)</Label>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                          <code className="text-green-300 text-xs font-mono break-all" data-testid={`rtmp-ingest-${ch.id}`}>
                            {isCombined ? combinedRtmp : ch.rtmp_url}
                          </code>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyText(isCombined ? combinedRtmp : ch.rtmp_url, `rtmp-${ch.id}`)}
                          className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                          data-testid={`copy-rtmp-${ch.id}`}
                        >
                          {copiedField === `rtmp-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      {!isCombined && ch.mux_stream_key && (
                        <div className="mt-2">
                          <Label className="text-gray-500 text-xs mb-1.5 block">RTMP Stream Key</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-900/80 border border-gray-700 rounded-md px-3 py-2.5">
                              <code className="text-green-300 text-xs font-mono break-all" data-testid={`rtmp-key-${ch.id}`}>{ch.mux_stream_key}</code>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyText(ch.mux_stream_key, `rtmp-key-${ch.id}`)}
                              className="h-10 w-10 p-0 text-gray-400 hover:text-white shrink-0"
                              data-testid={`copy-rtmp-key-${ch.id}`}
                            >
                              {copiedField === `rtmp-key-${ch.id}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Current assignment info */}
                    {ch.current_game && (
                      <div className="bg-sky-900/20 border border-sky-800/30 rounded-md px-3 py-2 mt-2">
                        <p className="text-sky-300 text-xs">
                          Currently assigned to: <span className="font-semibold text-white">{ch.current_game.title}</span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PullLinks;
